/**
 * Os estados que não são "deu certo".
 *
 * Existem como componentes próprios porque são a parte que o painel do R não
 * tinha: lá, filtro sem resultado e erro de leitura produziam a mesma tela em
 * branco. Aqui cada um diz o que aconteceu e, quando cabe, o que fazer.
 */

import type { ReactNode } from 'react'

import { ErroApi } from '../lib/api'
import { Botao } from './Botao'
import estilos from './Estado.module.css'

export function Carregando({ rotulo = 'Carregando…' }: { rotulo?: string }) {
  return (
    <div className={estilos.centro} role="status" aria-live="polite">
      <span className={estilos.pulso} aria-hidden="true" />
      <span className={estilos.texto}>{rotulo}</span>
    </div>
  )
}

/**
 * Erro de requisição.
 *
 * Mostra a mensagem que o backend escreveu — o `detail` de um 400 diz
 * exatamente qual filtro está malformado, e substituí-lo por um texto genérico
 * transforma um problema resolvível em mistério.
 */
export function Erro({
  erro,
  aoTentarDeNovo,
}: {
  erro: unknown
  aoTentarDeNovo?: () => void
}) {
  const ehApi = erro instanceof ErroApi
  const mensagem = ehApi
    ? erro.message
    : erro instanceof Error
      ? erro.message
      : 'Erro inesperado.'

  return (
    <div className={estilos.erro} role="alert">
      <div className={estilos.erroCorpo}>
        <strong className={estilos.erroTitulo}>
          {ehApi && erro.naoAutorizado ? 'Sem permissão' : 'Não deu para carregar'}
        </strong>
        <p className={estilos.erroMensagem}>{mensagem}</p>
        {ehApi && <span className={estilos.erroCodigo}>HTTP {erro.status}</span>}
      </div>
      {aoTentarDeNovo && (
        <Botao tamanho="pequeno" onClick={aoTentarDeNovo}>
          Tentar de novo
        </Botao>
      )}
    </div>
  )
}

/**
 * Recorte legítimo, sem linhas.
 *
 * Distinto de erro de propósito: "o filtro não pegou nada" é uma resposta, e o
 * operador precisa saber que a pergunta chegou ao servidor.
 */
export function Vazio({ titulo, children }: { titulo: string; children?: ReactNode }) {
  return (
    <div className={estilos.vazio}>
      <strong className={estilos.vazioTitulo}>{titulo}</strong>
      {children && <p className={estilos.vazioTexto}>{children}</p>}
    </div>
  )
}

export function Aviso({
  tom = 'atencao',
  children,
}: {
  tom?: 'atencao' | 'bom' | 'critico'
  children: ReactNode
}) {
  return (
    <div className={`${estilos.aviso} ${estilos[tom]}`} role="status">
      {children}
    </div>
  )
}
